// src/hooks/useResourceFilters.js
// Custom hook for managing resource filtering, search, and sorting state

import { useState, useMemo, useCallback } from 'react';

// Content type constants
export const CONTENT_TYPES = {
  ALL: 'all',
  ARTICLES: 'article',
  CASE_STUDIES: 'caseStudy',
  NEWS: 'news',
};

// Sort order constants
export const SORT_OPTIONS = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
  FEATURED: 'featured',
};

// Content type display labels
export const CONTENT_TYPE_LABELS = {
  [CONTENT_TYPES.ALL]: 'All',
  [CONTENT_TYPES.ARTICLES]: 'Articles',
  [CONTENT_TYPES.CASE_STUDIES]: 'Case Studies',
  [CONTENT_TYPES.NEWS]: 'News',
};

// Sort option display labels
export const SORT_LABELS = {
  [SORT_OPTIONS.NEWEST]: 'Newest First',
  [SORT_OPTIONS.OLDEST]: 'Oldest First',
  [SORT_OPTIONS.FEATURED]: 'Featured',
};

/**
 * Custom hook for filtering, searching, and sorting resources
 *
 * @param {array} resources - Array of resource objects
 * @returns {object} Filter state and handlers
 */
export const useResourceFilters = (resources = []) => {
  // Filter state
  const [search, setSearch] = useState('');
  const [contentType, setContentType] = useState(CONTENT_TYPES.ALL);
  const [sortOrder, setSortOrder] = useState(SORT_OPTIONS.NEWEST);
  const [activeTopics, setActiveTopics] = useState([]);
  const [activeAuthor, setActiveAuthor] = useState(null);

  // Memoized filtered and sorted data
  const filteredData = useMemo(() => {
    // Ensure resources is always an array (handles null/undefined)
    const safeResources = Array.isArray(resources) ? resources : [];
    let result = [...safeResources];

    // 1. Filter by content type
    if (contentType !== CONTENT_TYPES.ALL) {
      result = result.filter((r) => r.type === contentType);
    }

    // 2. Filter by search query
    if (search.trim()) {
      const query = search.toLowerCase().trim();
      result = result.filter((r) => {
        const topicNames = (r.topics || []).map(t => t?.name).filter(Boolean);
        const searchableFields = [
          r.title,
          r.summary,
          r.clientName,
          r.category,
          r.industry,
          ...topicNames,
          ...(r.tags || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchableFields.includes(query);
      });
    }

    // 3. Filter by active topics (intersection) - activeTopics stores topic names
    if (activeTopics.length > 0) {
      result = result.filter((r) =>
        r.topics?.some((topic) => activeTopics.includes(topic?.name))
      );
    }

    // 4. Filter by author
    if (activeAuthor) {
      result = result.filter((r) => r.author?.name === activeAuthor);
    }

    // 5. Sort
    result.sort((a, b) => {
      // Featured items first if sort is 'featured'
      if (sortOrder === SORT_OPTIONS.FEATURED) {
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
      }

      // Then by date
      const dateA = new Date(a.publishedAt);
      const dateB = new Date(b.publishedAt);

      return sortOrder === SORT_OPTIONS.OLDEST
        ? dateA - dateB
        : dateB - dateA;
    });

    return result;
  }, [resources, search, contentType, sortOrder, activeTopics, activeAuthor]);

  // Ensure resources is always an array for all calculations (memoized)
  const safeResourcesArray = useMemo(() => {
    return Array.isArray(resources) ? resources : [];
  }, [resources]);

  // Get featured resource (first one that's marked featured)
  const featuredResource = useMemo(() => {
    return safeResourcesArray.find((r) => r.isFeatured);
  }, [safeResourcesArray]);

  // Extract unique topics from resources (returns topic names for filter chips)
  const availableTopics = useMemo(() => {
    const topicsSet = new Set();
    safeResourcesArray.forEach((r) => {
      r.topics?.forEach((topic) => {
        if (topic?.name) topicsSet.add(topic.name);
      });
    });
    return Array.from(topicsSet).sort();
  }, [safeResourcesArray]);

  // Extract unique authors from resources
  const availableAuthors = useMemo(() => {
    const authorsMap = new Map();
    safeResourcesArray.forEach((r) => {
      if (r.author?.name) {
        authorsMap.set(r.author.name, r.author);
      }
    });
    return Array.from(authorsMap.values());
  }, [safeResourcesArray]);

  // Count by type
  const countsByType = useMemo(() => {
    return {
      all: safeResourcesArray.length,
      article: safeResourcesArray.filter((r) => r.type === 'article').length,
      caseStudy: safeResourcesArray.filter((r) => r.type === 'caseStudy').length,
      news: safeResourcesArray.filter((r) => r.type === 'news').length,
    };
  }, [safeResourcesArray]);

  // Toggle a topic in the active topics list
  const toggleTopic = useCallback((topic) => {
    setActiveTopics((prev) =>
      prev.includes(topic)
        ? prev.filter((t) => t !== topic)
        : [...prev, topic]
    );
  }, []);

  // Clear a single topic
  const clearTopic = useCallback((topic) => {
    setActiveTopics((prev) => prev.filter((t) => t !== topic));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearch('');
    setContentType(CONTENT_TYPES.ALL);
    setSortOrder(SORT_OPTIONS.NEWEST);
    setActiveTopics([]);
    setActiveAuthor(null);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      search.trim() !== '' ||
      contentType !== CONTENT_TYPES.ALL ||
      activeTopics.length > 0 ||
      activeAuthor !== null
    );
  }, [search, contentType, activeTopics, activeAuthor]);

  return {
    // Filter state
    filters: {
      search,
      contentType,
      sortOrder,
      activeTopics,
      activeAuthor,
    },

    // Setters
    setSearch,
    setContentType,
    setSortOrder,
    setActiveTopics,
    setActiveAuthor,
    toggleTopic,
    clearTopic,
    clearFilters,

    // Computed values
    filteredData,
    featuredResource,
    availableTopics,
    availableAuthors,
    countsByType,
    hasActiveFilters,

    // Counts
    totalCount: resources.length,
    filteredCount: filteredData.length,
  };
};

export default useResourceFilters;
